import { useCallback, useEffect, useRef } from 'react';

import { APIAdsTrackImpression } from '../../../api_ads_public';
import { useAds } from './AdsContext';


/**
 * Засчитывание показа по реальной видимости.
 *
 * Блок должен попасть во вьюпорт минимум наполовину и продержаться там
 * viewability_ms. Считать по монтированию нельзя: тогда показ пишется
 * рекламе, которую пользователь пролистал не глядя — рекламодатель
 * платит за воздух, а прогноз CTR на сервере обучается на мусоре.
 *
 * Возвращает ref для контейнера и время первого показа — оно нужно,
 * чтобы посчитать задержку до клика для антифрода.
 */
export default function useAdImpression(ad, { alwaysVisible = false, enabled = true } = {}) {
    const { token } = useAds();
    const ref = useRef(null);
    const shownAtRef = useRef(null);
    const firedRef = useRef(false);

    useEffect(() => {
        shownAtRef.current = null;
        firedRef.current = false;
        if (!ad?.token || !enabled) return undefined;
        let alive = true;
        let visible = alwaysVisible;
        let timer = null;
        let retry = null;
        let pending = false;
        let observer;
        const delay = Math.max(0, Number(ad.viewability_ms || 1000));
        const stop = () => {
            clearTimeout(timer); timer = null;
            clearTimeout(retry); retry = null;
        };
        const send = async () => {
            timer = null;
            if (!alive || !visible || document.hidden || firedRef.current || pending) return;
            pending = true;
            const result = await APIAdsTrackImpression(token, ad.token);
            pending = false;
            if (!alive) return;
            if (result?.success) {
                firedRef.current = true;
            } else if (result?.error !== 'bad_token') {
                retry = setTimeout(schedule, 1000);
            }
        };
        const schedule = () => {
            if (!alive || !visible || document.hidden) { stop(); return; }
            if (firedRef.current || pending || timer) return;
            if (!shownAtRef.current) shownAtRef.current = Date.now();
            timer = setTimeout(send, delay);
        };
        const onVisibility = () => {
            if (document.hidden) { stop(); shownAtRef.current = null; }
            else schedule();
        };
        document.addEventListener('visibilitychange', onVisibility);
        if (alwaysVisible || typeof IntersectionObserver === 'undefined') {
            visible = true;
            schedule();
        } else if (ref.current) {
            observer = new IntersectionObserver(([entry]) => {
                visible = Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.5);
                if (!visible) { stop(); shownAtRef.current = null; }
                else schedule();
            }, { threshold: [0, 0.5, 1] });
            observer.observe(ref.current);
        }
        return () => {
            alive = false;
            stop();
            observer?.disconnect();
            document.removeEventListener('visibilitychange', onVisibility);
        };
    }, [ad?.token, ad?.viewability_ms, alwaysVisible, enabled, token]);

    const dwellMs = useCallback(() => shownAtRef.current
        ? Date.now() - shownAtRef.current : null, []);
    return { ref, dwellMs, wasShown: () => firedRef.current };
}